// Data persistence layer using localStorage
const STORE_PREFIX = 'prl_';

export interface SiteConfig {
  siteName: string;
  domain: string;
  companyName: string;
  legalName: string;
  themeColor: string;
  accentColor: string;
}

export interface ApiKeys {
  googleAnalyticsId: string;
  googleAdsId: string;
  googleTagManagerId: string;
  bingApiKey: string;
  googleMapsApiKey: string;
  facebookAppId: string;
}

export interface PaymentConfig {
  stripePublicKey: string;
  stripeSecretKey: string;
  paypalClientId: string;
  paypalSecret: string;
  squareApplicationId: string;
  squareAccessToken: string;
  squareEnvironment: 'sandbox' | 'production';
}

export interface WebAppConfig {
  deploymentId: string;
  webAppUrl: string;
  libraryUrl: string;
  scriptId: string;
}

export interface OfficeAddress {
  id: string;
  label: string;
  name: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
  phone: string;
  email: string;
  hours: string;
  isActive: boolean;
}

export interface PageContent {
  title: string;
  description: string;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  h1: string;
  bodyContent: string;
  lastEdited: string;
}

export interface DiagnosticsResult {
  name: string;
  status: 'pass' | 'fail' | 'warning' | 'pending';
  message: string;
  timestamp: string;
}

function get<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(STORE_PREFIX + key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function set<T>(key: string, value: T): void {
  localStorage.setItem(STORE_PREFIX + key, JSON.stringify(value));
}

// Site Config
export const siteConfig = {
  get: (): SiteConfig => get<SiteConfig>('siteConfig', {
    siteName: 'Projector Repair London',
    domain: 'projectorrepairlondon.co.uk',
    companyName: 'Wells Electronics Ltd',
    legalName: 'Wells Electronics Ltd',
    themeColor: '#0057B7',
    accentColor: '#FFDD00',
  }),
  set: (v: SiteConfig) => set('siteConfig', v),
};

// API Keys
export const apiKeys = {
  get: (): ApiKeys => get<ApiKeys>('apiKeys', {
    googleAnalyticsId: 'G-XXXXXXXXXX',
    googleAdsId: 'AW-XXXXXXXXX',
    googleTagManagerId: 'GTM-XXXXXXX',
    bingApiKey: '',
    googleMapsApiKey: '',
    facebookAppId: 'your-fb-app-id',
  }),
  set: (v: ApiKeys) => set('apiKeys', v),
};

// Payment Config
export const paymentConfig = {
  get: (): PaymentConfig => get<PaymentConfig>('paymentConfig', {
    stripePublicKey: '',
    stripeSecretKey: '',
    paypalClientId: '',
    paypalSecret: '',
    squareApplicationId: '',
    squareAccessToken: '',
    squareEnvironment: 'sandbox',
  }),
  set: (v: PaymentConfig) => set('paymentConfig', v),
};

// Web App Config
export const webAppConfig = {
  get: (): WebAppConfig => get<WebAppConfig>('webAppConfig', {
    deploymentId: 'AKfycbxaG4ZQ2dc9wIJdHrfnoHDr70GUMPX3oknIwpbJFE3N-5nmVW5X8RLyrxsqpiYr8u-Liw',
    webAppUrl: 'https://script.google.com/macros/s/AKfycbxaG4ZQ2dc9wIJdHrfnoHDr70GUMPX3oknIwpbJFE3N-5nmVW5X8RLyrxsqpiYr8u-Liw/exec',
    libraryUrl: 'https://script.google.com/macros/library/d/1bx7KMweFKPW0cPOP9V2c6o4JMxBMwgYErEHIhLJIDZiPvspSl0uD2RcU/8',
    scriptId: '',
  }),
  set: (v: WebAppConfig) => set('webAppConfig', v),
};

// Addresses
export const addresses = {
  get: (): OfficeAddress[] => get<OfficeAddress[]>('addresses', [
    {
      id: 'uk-hq',
      label: 'UK HQ',
      name: 'Projector Repair London (UK HQ)',
      line1: 'Unit 9, West Moor Park',
      line2: 'Yorkshire Way',
      city: 'Doncaster',
      region: 'England',
      postcode: 'DN3 3GW',
      country: 'United Kingdom',
      phone: '0333 006 4326',
      email: 'wellselectronicsltd@gmail.com',
      hours: 'Mon\u2013Fri / 9:00\u201317:00',
      isActive: true,
    },
    {
      id: 'ie-office',
      label: 'Ireland',
      name: 'Projector Repair Ireland',
      line1: 'Unit 7b, Universal Business Park',
      line2: '',
      city: 'Bluebell, Dublin',
      region: '',
      postcode: 'D12',
      country: 'Ireland',
      phone: '+353 1 582 7344',
      email: 'wellselectronicsltd@gmail.com',
      hours: 'Mon\u2013Fri / 9:00\u201317:00',
      isActive: true,
    },
    {
      id: 'emea-hub',
      label: 'EMEA Hub',
      name: 'Projector Repair EMEA',
      line1: 'Wells Electronics EMEA',
      line2: 'Amsterdam Business Park',
      city: 'Amsterdam',
      region: 'Noord-Holland',
      postcode: '1012 AB',
      country: 'Netherlands',
      phone: '',
      email: 'wellselectronicsltd@gmail.com',
      hours: 'Mon\u2013Fri / 9:00\u201317:00 CET',
      isActive: false,
    },
  ]),
  set: (v: OfficeAddress[]) => set('addresses', v),
};

// Page Content
export const pageContent = {
  get: (pageId: string): PageContent => get<PageContent>(`page_${pageId}`, {
    title: '',
    description: '',
    keywords: '',
    ogTitle: '',
    ogDescription: '',
    h1: '',
    bodyContent: '',
    lastEdited: new Date().toISOString(),
  }),
  set: (pageId: string, v: PageContent) => set(`page_${pageId}`, v),
};

// Diagnostics Results
export const diagnostics = {
  get: (): DiagnosticsResult[] => get<DiagnosticsResult[]>('diagnostics', []),
  set: (v: DiagnosticsResult[]) => set('diagnostics', v),
};
